"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  CAO_MATHS_BONUS,
  CAO_MAX_POINTS,
  type CaoLevel,
  type CaoScheme,
  type CaoSubject,
  caoBasePoints,
  caoGrades,
  caoTotal,
  earnsMathsBonus,
  LCVP_GRADES,
  LCVP_POINTS,
  type LcvpGrade,
} from "@/lib/tool-kit/intl-grades/cao-points";

/** English strings. A translated content file overrides these through `ui`. */
export const CAO_POINTS_CALCULATOR_STRINGS = {
  schemeLabel: "When did you sit the Leaving Certificate?",
  schemes: {
    current: "2017 or later (grades H1–H8, O1–O8)",
    legacy: "1992–2016 (grades A1–NG)",
  },
  subjectsHeading: "Your subjects",
  subjectLabel: "Subject {n} (optional name)",
  subjectPlaceholder: "Subject {n}",
  levelLabel: "Level {n}",
  levels: {
    higher: "Higher",
    ordinary: "Ordinary",
  },
  gradeLabel: "Grade {n}",
  gradeOption: "{grade} — {points} points",
  mathsLabel: "This is Higher Level Mathematics (adds the bonus)",
  addSubject: "Add a subject",
  removeSubject: "Remove",
  removeSubjectAria: "Remove subject {n}",
  lcvpLabel: "LCVP Link Modules (optional)",
  lcvpNone: "Not taken",
  lcvpGrades: {
    distinction: "Distinction",
    merit: "Merit",
    pass: "Pass",
  },
  lcvpOption: "{grade} — {points} points",
  results: "Result",
  total: "Total CAO points",
  maxNote: "out of a possible {max}",
  withoutBonus: "Without the Maths bonus",
  bonusBenefit: "Maths bonus added",
  counted: "Subjects counted",
  bonusMissed:
    "Your Higher Level Mathematics score is not in your best six, so the {bonus}-point bonus adds nothing.",
  bonusNone:
    "No bonus applies: the Mathematics row needs Higher Level and a grade that earns it.",
  fewSubjects: "Only {count} scored so far — CAO counts your best six.",
  breakdown: "Points by subject",
  colSubject: "Subject",
  colResult: "Level and grade",
  colPoints: "Points",
  colCounted: "In best six",
  yes: "Yes",
  no: "No",
  lcvpName: "LCVP Link Modules",
  emptyPrompt: "Add at least one subject to see your points.",
  caveat:
    "Medicine and some other courses use extra scoring rules, and a course may require specific subjects or grades whatever your total. Check the CAO Handbook and the course entry.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "CAO points (best six): {total}",
} as const;

type Strings = typeof CAO_POINTS_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Row extends CaoSubject {
  name: string;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, name: "", level: "higher", grade: "H3", maths: true },
  { id: 2, name: "", level: "higher", grade: "H2", maths: false },
  { id: 3, name: "", level: "higher", grade: "H4", maths: false },
  { id: 4, name: "", level: "higher", grade: "H2", maths: false },
  { id: 5, name: "", level: "higher", grade: "H3", maths: false },
  { id: 6, name: "", level: "higher", grade: "H3", maths: false },
  { id: 7, name: "", level: "higher", grade: "H5", maths: false },
];

const LEVELS: readonly CaoLevel[] = ["higher", "ordinary"];
const MAX_ROWS = 10;
/** The number in a current grade code (H3 → "3"), used to carry a grade across levels. */
const GRADE_NUMBER = /\d+$/;

/** The same grade on the other level when the scheme has one (H3 → O3); else the best grade. */
function carryGrade(scheme: CaoScheme, level: CaoLevel, grade: string): string {
  const grades = caoGrades(scheme, level);
  if (grades.includes(grade)) {
    return grade;
  }
  const digits = GRADE_NUMBER.exec(grade)?.[0] ?? "";
  const match = grades.find((candidate) => candidate.endsWith(digits));
  return digits && match ? match : (grades[0] ?? "");
}

function defaultGrade(scheme: CaoScheme, level: CaoLevel): string {
  return caoGrades(scheme, level)[0] ?? "";
}

/** The grade on the other scheme that scores closest to this one (H3 = 77 → B2). */
function convertGrade(
  from: CaoScheme,
  to: CaoScheme,
  level: CaoLevel,
  grade: string
): string {
  const target = caoBasePoints(from, level, grade) ?? 0;
  let best = defaultGrade(to, level);
  let bestGap = Number.POSITIVE_INFINITY;
  for (const candidate of caoGrades(to, level)) {
    const gap = Math.abs((caoBasePoints(to, level, candidate) ?? 0) - target);
    if (gap < bestGap) {
      best = candidate;
      bestGap = gap;
    }
  }
  return best;
}

function SubjectRow({
  row,
  index,
  scheme,
  strings,
  fmt,
  onChange,
  onMaths,
  onRemove,
}: {
  row: Row;
  index: number;
  scheme: CaoScheme;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onMaths: (checked: boolean) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="grid items-end gap-3 sm:grid-cols-[1.5fr_1fr_1.2fr_auto]">
        <TextField
          id={`cao-name-${row.id}`}
          label={t(strings.subjectLabel, { n })}
          onChange={(name) => onChange({ ...row, name })}
          placeholder={t(strings.subjectPlaceholder, { n })}
          value={row.name}
        />
        <SelectField
          id={`cao-level-${row.id}`}
          label={t(strings.levelLabel, { n })}
          onChange={(level: CaoLevel) =>
            onChange({
              ...row,
              level,
              grade: carryGrade(scheme, level, row.grade),
              maths: level === "higher" ? row.maths : false,
            })
          }
          options={LEVELS.map((value) => ({
            value,
            label: strings.levels[value],
          }))}
          value={row.level}
        />
        <SelectField
          id={`cao-grade-${row.id}`}
          label={t(strings.gradeLabel, { n })}
          onChange={(grade) => onChange({ ...row, grade })}
          options={caoGrades(scheme, row.level).map((grade) => ({
            value: grade,
            label: t(strings.gradeOption, {
              grade,
              points: fmt(caoBasePoints(scheme, row.level, grade) ?? 0),
            }),
          }))}
          value={row.grade}
        />
        <button
          aria-label={t(strings.removeSubjectAria, { n })}
          className={BUTTON_SECONDARY}
          onClick={onRemove}
          type="button"
        >
          {strings.removeSubject}
        </button>
      </div>
      {row.level === "higher" ? (
        <Checkbox
          checked={row.maths}
          label={strings.mathsLabel}
          onChange={onMaths}
        />
      ) : null}
    </div>
  );
}

export default function CaoPointsCalculatorWidget() {
  const strings = useToolUi(CAO_POINTS_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [scheme, setScheme] = useState<CaoScheme>("current");
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(8);
  const [lcvp, setLcvp] = useState<LcvpGrade | "none">("none");

  const result = caoTotal(scheme, rows, lcvp === "none" ? null : lcvp);
  const mathsRow = rows.find((row) => row.maths);
  const mathsScored = result.scored.find((item) => item.id === mathsRow?.id);

  function changeScheme(next: CaoScheme) {
    setScheme(next);
    setRows((current) =>
      current.map((row) => ({
        ...row,
        grade: convertGrade(scheme, next, row.level, row.grade),
      }))
    );
  }

  function setMaths(id: number, checked: boolean) {
    setRows((current) =>
      current.map((row) => ({ ...row, maths: row.id === id ? checked : false }))
    );
  }

  const nameOf = (row: Row, index: number) =>
    row.name.trim() || t(strings.subjectPlaceholder, { n: fmt(index + 1) });

  const tableRows = [
    ...rows.flatMap((row, index) => {
      const item = result.scored.find((scored) => scored.id === row.id);
      if (!item) {
        return [];
      }
      return [
        [
          nameOf(row, index),
          `${strings.levels[row.level]} ${row.grade}`,
          item.bonus > 0
            ? `${fmt(item.base)} + ${fmt(item.bonus)} = ${fmt(item.score)}`
            : fmt(item.score),
          item.counted ? strings.yes : strings.no,
        ],
      ];
    }),
    ...(lcvp === "none"
      ? []
      : [
          [
            strings.lcvpName,
            strings.lcvpGrades[lcvp],
            fmt(LCVP_POINTS[lcvp]),
            result.scored.find((scored) => scored.id === -1)?.counted
              ? strings.yes
              : strings.no,
          ],
        ]),
  ];

  const bonusEligible =
    mathsRow !== undefined && earnsMathsBonus(scheme, mathsRow.grade);

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.schemeLabel}
          name="cao-scheme"
          onChange={changeScheme}
          options={(["current", "legacy"] as const).map((value) => ({
            value,
            label: strings.schemes[value],
          }))}
          value={scheme}
        />
        <h3 className={WIDGET_H3}>{strings.subjectsHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <SubjectRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onMaths={(checked) => setMaths(row.id, checked)}
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              scheme={scheme}
              strings={strings}
            />
          ))}
        </div>
        {rows.length < MAX_ROWS ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setRows((current) => [
                ...current,
                {
                  id: nextId,
                  name: "",
                  level: "higher",
                  grade: defaultGrade(scheme, "higher"),
                  maths: false,
                },
              ]);
              setNextId((value) => value + 1);
            }}
            type="button"
          >
            {strings.addSubject}
          </button>
        ) : null}
        <SelectField
          id="cao-lcvp"
          label={strings.lcvpLabel}
          onChange={setLcvp}
          options={[
            { value: "none" as const, label: strings.lcvpNone },
            ...LCVP_GRADES.map((value) => ({
              value,
              label: t(strings.lcvpOption, {
                grade: strings.lcvpGrades[value],
                points: fmt(LCVP_POINTS[value]),
              }),
            })),
          ]}
          value={lcvp}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result.subjectCount === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.total}
                value={fmt(result.total)}
              />
              <StatCard
                label={strings.withoutBonus}
                value={fmt(result.totalWithoutBonus)}
              />
              <StatCard
                label={strings.bonusBenefit}
                value={fmt(result.bonusBenefit)}
              />
            </StatGrid>
            <p className="text-muted-foreground text-sm">
              {t(strings.maxNote, { max: fmt(CAO_MAX_POINTS) })}
            </p>
            {mathsRow && !bonusEligible ? (
              <Notice>{strings.bonusNone}</Notice>
            ) : null}
            {mathsRow &&
            bonusEligible &&
            mathsScored &&
            !mathsScored.counted ? (
              <Notice>
                {t(strings.bonusMissed, { bonus: fmt(CAO_MATHS_BONUS) })}
              </Notice>
            ) : null}
            {result.subjectCount < 6 ? (
              <p className="text-muted-foreground text-sm">
                {t(strings.fewSubjects, { count: fmt(result.subjectCount) })}
              </p>
            ) : null}
            <DataTable
              caption={strings.breakdown}
              head={[
                strings.colSubject,
                strings.colResult,
                strings.colPoints,
                strings.colCounted,
              ]}
              rows={tableRows}
            />
            <p className="text-muted-foreground text-sm">{strings.caveat}</p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, { total: fmt(result.total) })}
            />
          </>
        )}
      </section>
    </div>
  );
}
